import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Badge, Button, Card, Dialog, Flex, Heading, IconButton, Text } from '@radix-ui/themes'
import { MobileIcon } from '@radix-ui/react-icons'
import { Ops } from '@willie/schemas'
import { ConfirmActionButton } from '~/components/ConfirmAction'
import { CopyButton } from '~/components/CopyButton'
import { useTRPC } from '~/utils/trpc'
import { formatTimestamp } from '~/utils/date'
import type { InstallationWithRelations } from '../service'
import { useInvalidateCompatibility } from '../useInvalidateCompatibility'
import { CompatibilityBadge } from './CompatibilityBadge'
import { CompatibilityVerdictForm } from './CompatibilityVerdictForm'
import { InstallLinkDialog } from './InstallLinkDialog'
import { LoggerMakerBadge } from './LoggerMakerBadge'

const GuideLinkButton = ({ installationId, profile }: { installationId: number; profile: string | null }) => {
  const hasGuide = profile ? Ops.InstallationProfiles.hasInstallationGuide(profile) : false
  return (
    <CopyButton
      iconOnly
      disabled={!hasGuide}
      value={profile ? Ops.StationInstallations.buildInstallationGuideUrl(profile, installationId) : ''}
      label={hasGuide ? 'Copy documentation link' : 'No installation guide exists for this profile'}
    />
  )
}

const ApproveUnsupportedProfileButton = ({ installation }: { installation: InstallationWithRelations }) => {
  const trpc = useTRPC()
  const invalidate = useInvalidateCompatibility()
  const approve = useMutation(
    trpc.ops.stationInstallations.approveUnsupportedProfile.mutationOptions({ onSuccess: invalidate })
  )

  return (
    <ConfirmActionButton
      label="Approve"
      size="1"
      variant="soft"
      color="orange"
      onConfirm={() => approve.mutateAsync({ installationId: installation.installation_id }).then(() => undefined)}
      confirm={{
        title: `Approve ${installation.installation_profile} for this installation?`,
        description:
          'The profile is not supported. Approving lets ops order this one installation on it, and records you as the approver. Changing the profile later withdraws the approval.',
        actionLabel: 'Approve',
        pending: approve.isPending,
        errorMessage: approve.error?.message ?? null,
        onOpen: () => approve.reset()
      }}
    />
  )
}

export const CompatibilityCard = ({ installation }: { installation: InstallationWithRelations }) => {
  const [open, setOpen] = useState(false)
  const status = installation.compatibility_status
  const profile = installation.installation_profile
  const locked = Ops.StationInstallations.compatibilityLockReason(installation)
  const hasVerdict = Ops.StationInstallations.hasCompatibilityVerdict(status)
  const canApprove = status === 'compatible_not_supported' && profile !== null && locked === null
  const note =
    status === 'compatible_approved' && installation.unsupported_profile_approved_at
      ? `Approved by ${installation.unsupported_profile_approved_by_email ?? 'unknown'} on ${formatTimestamp(installation.unsupported_profile_approved_at)}`
      : locked

  return (
    <Card>
      <Flex direction="column" gap="3">
        <Flex align="center" justify="between" gap="3">
          <Heading size="3">Compatibility</Heading>
          <Flex align="center" gap="3">
            <GuideLinkButton installationId={installation.installation_id} profile={profile} />
            <InstallLinkDialog
              installationId={installation.installation_id}
              trigger={
                <IconButton
                  type="button"
                  size="1"
                  variant="ghost"
                  title="Show the install link the customer gets"
                  aria-label="Show install link"
                >
                  <MobileIcon />
                </IconButton>
              }
            />
            {canApprove ? <ApproveUnsupportedProfileButton installation={installation} /> : null}
            <Dialog.Root open={open} onOpenChange={setOpen}>
              <Dialog.Trigger>
                <Button type="button" size="1" variant="soft" disabled={locked !== null}>
                  {hasVerdict ? 'Edit' : 'Check compatibility'}
                </Button>
              </Dialog.Trigger>
              <Dialog.Content maxWidth="640px">
                <Dialog.Title>Compatibility check</Dialog.Title>
                <Dialog.Description size="2" color="gray" mb="4">
                  A compatible meter needs a profile. An unsupported profile blocks the order until the hardware
                  team approves it.
                </Dialog.Description>
                <CompatibilityVerdictForm
                  installationId={installation.installation_id}
                  initial={{
                    meterMaker: installation.meter_maker,
                    meterModel: installation.meter_model,
                    installationProfile: profile,
                    notCompatible: installation.meter_compatibility === 'not_compatible'
                  }}
                  submitLabel="Save"
                  onSaved={() => setOpen(false)}
                />
              </Dialog.Content>
            </Dialog.Root>
          </Flex>
        </Flex>

        <Flex align="center" gap="2" wrap="wrap">
          <CompatibilityBadge status={status} size="2" />
          <Badge color="gray" variant="soft" size="2" className="font-mono">
            {profile ?? 'No profile'}
          </Badge>
          <LoggerMakerBadge loggerMaker={installation.logger_maker} color="gray" size="2" />
        </Flex>

        {note ? (
          <Text size="1" color="gray">
            {note}
          </Text>
        ) : null}
      </Flex>
    </Card>
  )
}
